import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusEvent } from '@chancenwerk/campus-learning-content/entities/CampusEvent.js';
import { CampusEventFilter } from '@chancenwerk/campus-learning-content/entities/CampusEventFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { CampusRoutes } from '../routes.js';
/**
 * @customElement campus-page-administration-list-event
 */
@customElement('campus-page-administration-list-event')
export class CampusPageAdministrationListEvent extends CrPage {
  constructor() {
    super();
    this.label = 'Events';
    this.permission = ChancenPermission.campus.ADMIN_EVENTS;
    this.stickyHeader = true;
    this.topScrollButton = true;
    this.noBottomPadding = true;
  }

  @state() private readonly _filter = new RXController(this, new CampusEventFilter({}));

  override header() {
    return html` <cr-button icon="add" href="${CampusRoutes.eventAdmin + `/edit`}"> Event </cr-button> `;
  }

  override content() {
    return html`
      <cr-card margin="xs">
        <cr-input
          placeholder="Suchen"
          icon="search"
          clearable
          @value-changed=${(e: ValueEvent<string>) => {
            this._filter.next(new CampusEventFilter({ search: e.detail.value }));
          }}
        ></cr-input>
      </cr-card>
      <cr-data-view
        sortable
        ${css({ paddingTop: this.spacing.md })}
        .options=${{
          class: CampusEvent,
          view: new View({ filter: this._filter.value }),
          selectionSet: CampusEvent.allFields,
          operation: LearningContentAPI.subscribeEventsByFilter,
          rowHref: entityInstance => CampusRoutes.eventAdmin + '/edit/' + entityInstance._id,
          emptyState: () =>
            html`<cr-flex crossaxis-center padding="md" ${css({ gridColumn: '1/-1' })}>
              <cr-text>Keine Events vorhanden</cr-text>
            </cr-flex>`,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: 'Titel',
              fieldName: 'title',
              getFieldMapping: entityInstance => entityInstance.title ?? '',
            },
            {
              columnName: 'Beschreibung',
              fieldName: 'description',
              getFieldMapping: entityInstance =>
                entityInstance.description && entityInstance.description.length > 20
                  ? entityInstance.description.slice(0, 19) + '...'
                  : entityInstance.description?.slice(0, 19),
            },
            {
              columnName: 'Laufzeit',
              fieldName: 'startDate',
              getFieldMapping: entity => {
                if (!entity.startDate) return '?';
                if (!entity.endDate) return `Seit: ${entity.startDate.toFormat('dd.LL.yyyy')}`;
                return `${entity.startDate.toFormat('dd.LL.yyyy')} - ${entity.endDate.toFormat('dd.LL.yyyy')}`;
              },

              columnWidth: '200px',
            },
          ],
        } satisfies DataViewOptions<typeof CampusEvent>}
      ></cr-data-view>
    `;
  }
}
